import { supabase } from '@/core/database/supabase';
import { permissionCatalog, permissionKeysToJson } from '@/modules/settings/permissions/catalog/permissionCatalog';
import { enforceSettingsPermission } from '@/modules/settings/permissions/enforceSettingsPermission';
import type { RoleFormData, RoleTemplate } from '../types/rolesNew.types';

function toTitleCase(value: string) {
  return value
    .split(/[^0-9a-zA-Z]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('-');
}

export const roleTemplates: RoleTemplate[] = [
  {
    code: 'warehouse-manager',
    name: 'Quản lý kho',
    description: 'Quản lý vật tư, tồn kho, nhập xuất và duyệt phiếu kho.',
    permissionKeys: [
      'inventory.items.view',
      'inventory.items.create',
      'inventory.items.update',
      'inventory.stock.view',
      'inventory.stock.import',
      'inventory.stock.export',
      'inventory.stock.adjust',
      'inventory.stock.approve',
      'reports.analytics.view',
    ],
  },
  {
    code: 'project-manager',
    name: 'Quản lý dự án',
    description: 'Quản lý hồ sơ, tiến độ, chi phí và báo cáo dự án.',
    permissionKeys: [
      'projects.records.view',
      'projects.records.create',
      'projects.records.update',
      'projects.costs.view',
      'projects.costs.update',
      'projects.costs.approve',
      'reports.analytics.view',
      'reports.analytics.export',
    ],
  },
  {
    code: 'operator',
    name: 'Nhân viên vận hành',
    description: 'Xem dữ liệu cần thiết và thao tác nghiệp vụ cơ bản.',
    permissionKeys: [
      'inventory.items.view',
      'inventory.stock.view',
      'projects.records.view',
      'reports.analytics.view',
    ],
  },
];

export const rolesNewService = {
  getAvailablePermissions() {
    return permissionCatalog;
  },

  getTemplates() {
    return roleTemplates;
  },

  async createRole(data: RoleFormData): Promise<void> {
    await enforceSettingsPermission('settings.roles.create');
    const { error } = await supabase
      .from('roles')
      .insert({
        code: data.code,
        name: data.name.trim(),
        description: data.description.trim() || null,
        permissions_json: data.permissionsJson,
      });

    if (error) throw error;
  },

  async generateUniqueCode(name: string): Promise<string> {
    const base = this.slugify(name);
    if (!base) return '';

    const { data, error } = await supabase
      .from('roles')
      .select('code')
      .ilike('code', `${base}%`);

    if (error) throw error;
    const usedCodes = new Set((data || []).map((row: { code: string }) => row.code));
    if (!usedCodes.has(base)) return base;

    let index = 2;
    while (usedCodes.has(`${base}-${index}`)) index += 1;
    return `${base}-${index}`;
  },

  applyTemplate(template: RoleTemplate): RoleFormData {
    return {
      code: this.slugify(template.name),
      name: template.name,
      description: template.description,
      permissionsJson: permissionKeysToJson(template.permissionKeys),
    };
  },

  slugify(text: string): string {
    return toTitleCase(
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[đĐ]/g, 'd'),
    );
  },
};
